import { createFileRoute, Navigate, Link, useNavigate, useLocation } from '@tanstack/react-router';
import { Settings2, Check } from 'lucide-react';
import { useState } from 'react';
import { useAtom } from 'jotai';
import { useTestMode } from '@/hooks/useTestMode';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { toast } from '@/hooks/useToast';
import { useSaveSetupChecklistMutation } from '@/lib/query-options';
import { checklistFromTicks, ticksFromChecklist, type SetupTicks } from '@/lib/setup-checklist';
import { Card, CardContent } from '@/components/ui/card';
import { TabCardPanel, tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { GoalsPage } from '@/components/strategy/goals-page';
import { organizationDataAtom, selectedSetupTabAtom } from '@/store';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/_layout/setup/')({
  component: SetupPageWrapper,
  staticData: {
    title: 'Setup',
    icon: Settings2,
  },
});

function SetupPageWrapper() {
  const isTestMode = useTestMode();
  if (!isTestMode) return <Navigate to="/campaigns" />;
  return <SetupPage />;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type DescPart = { text: string } | { label: string; to: string };

interface SubStep {
  id: string;
  title: string;
  description: DescPart[];
  optional?: boolean;
  locked?: boolean;
}

interface Step {
  id: string;
  title: string;
  description: DescPart[];
  forceComplete?: boolean;
  children?: SubStep[];
}

interface StepDetail {
  intro: string;
  involved: string[];
  benefits: string[];
}

// ─── Step Data ────────────────────────────────────────────────────────────────

const STEPS: Step[] = [
  {
    id: 'signup',
    title: 'Sign up',
    description: [{ text: 'Account created and verified.' }],
    forceComplete: true,
  },
  {
    id: 'business',
    title: 'Create business',
    description: [
      { text: 'Set up your business profile including name, website, and industry on the ' },
      { label: 'Business page', to: '/business' },
      { text: '.' },
    ],
  },
  {
    id: 'providers',
    title: 'Connect providers',
    description: [{ text: 'Grant platform access to your advertising accounts via OAuth.' }],
    children: [
      {
        id: 'providers-google',
        title: 'Google Ads',
        description: [
          { text: 'Authorise Google Ads and choose your ad account in ' },
          { label: 'Account settings', to: '/settings/account' },
          { text: '.' },
        ],
      },
      {
        id: 'providers-meta',
        title: 'Meta',
        description: [{ text: 'Authorise Meta Ads via OAuth.' }],
        locked: true,
      },
    ],
  },
  {
    id: 'conversion-tracking',
    title: 'Set up conversion tracking',
    description: [{ text: 'Install tracking tags on your website and verify they are firing correctly.' }],
    children: [
      {
        id: 'conversion-tags',
        title: 'Install conversion tags',
        description: [{ text: 'Add the Google tag to your website before launching any campaigns.' }],
      },
      {
        id: 'hubspot-offline',
        title: 'Create offline events',
        description: [
          { text: 'Sync HubSpot CRM deal events as offline conversions — configure in ' },
          { label: 'Account settings', to: '/settings/account' },
          { text: '.' },
        ],
        optional: true,
      },
    ],
  },
  {
    id: 'campaigns',
    title: 'Create campaigns',
    description: [{ text: 'Build your first set of campaigns across campaign types.' }],
    children: [
      {
        id: 'campaigns-brand',
        title: 'Brand campaign',
        description: [
          { text: 'Protect your brand terms with exact-match keywords using the ' },
          { label: 'brand campaign wizard', to: '/new/brand' },
          { text: '.' },
        ],
      },
      {
        id: 'campaigns-prospecting',
        title: 'Prospecting or HiPo campaign',
        description: [
          { text: 'Target potential customers across the top, middle, and bottom of the funnel via the ' },
          { label: 'campaign wizard', to: '/new/campaign' },
          { text: '.' },
        ],
      },
    ],
  },
  {
    id: 'assets',
    title: 'Add ad assets',
    description: [
      { text: 'Configure sitelinks, callouts, and structured snippets in the ' },
      { label: 'Assets section', to: '/assets' },
      { text: ' before going live.' },
    ],
  },
  {
    id: 'budget-cap',
    title: 'Set organisation budget cap',
    description: [
      { text: 'Define the maximum monthly spend across all campaigns on the ' },
      { label: 'Business page', to: '/business' },
      { text: '.' },
    ],
  },
  {
    id: 'review',
    title: 'Review and approve ad copy',
    description: [
      { text: 'Check headlines and descriptions across all ' },
      { label: 'campaigns', to: '/campaigns' },
      { text: ' before launch.' },
    ],
  },
  {
    id: 'live',
    title: 'Go live',
    description: [
      { text: 'Publish campaigns and start serving ads from ' },
      { label: 'Campaigns', to: '/campaigns' },
      { text: '.' },
    ],
  },
  {
    id: 'search-terms',
    title: 'Review search terms',
    description: [
      { text: 'Check what searches triggered your ads and add negative keywords to cut wasted spend — visible in ' },
      { label: 'Campaigns', to: '/campaigns' },
      { text: '.' },
    ],
  },
  {
    id: 'performance-review',
    title: 'Review performance',
    description: [
      { text: 'Check CTR, CPA, and conversion volume after the first 7 days and generate your first ' },
      { label: 'report', to: '/reports' },
      { text: '.' },
    ],
  },
  {
    id: 'optimizations-review',
    title: 'Act on optimizations',
    description: [
      { text: "Review and apply the system's first batch of recommendations in " },
      { label: 'Optimizations', to: '/optimizations' },
      { text: '.' },
    ],
  },
];

// ─── Step Details ─────────────────────────────────────────────────────────────

const STEP_DETAILS: Record<string, StepDetail> = {
  signup: {
    intro: 'Your account has been created and verified. This gives you a secure, isolated workspace on the platform.',
    involved: [
      'Email verification and password setup',
      'Organisation workspace created',
      'Ability to invite team members',
    ],
    benefits: [
      'Full access to all platform features for your organisation',
      'Role-based permissions so team members only see what they need',
      'Audit trail and activity logging from day one',
    ],
  },
  business: {
    intro: 'The business profile is the foundation everything else is built on. It drives campaign creation, keyword research and asset generation.',
    involved: [
      'Business name, website URL, and industry',
      'Target audience and geographic focus',
      'Brand tone and key messaging',
      'Budget preferences for campaign planning',
    ],
    benefits: [
      'Generated campaigns, headlines and keywords are personalised to your business, not generic',
      'A complete profile improves everything Glitch generates across the platform',
      'Changing this later updates recommendations and suggestions automatically',
    ],
  },
  providers: {
    intro: 'Connecting your advertising providers grants the platform read and write access to create, manage, and optimise campaigns on your behalf.',
    involved: [
      'Google OAuth authorisation — grants access to your Google Ads account',
      'Linking your specific Google Ads customer account ID',
      'Meta OAuth authorisation (coming soon)',
    ],
    benefits: [
      'Without this step, no campaigns can be pushed live or managed through the platform',
      'Google OAuth also unlocks keyword performance data, search term reports, and real-time campaign metrics',
      'Access is revocable at any time from your Google account permissions',
    ],
  },
  'conversion-tracking': {
    intro: 'Conversion tracking is the single most important technical setup step. Without it, Google\'s bidding algorithms have no signal to optimise toward and ROI cannot be measured.',
    involved: [
      'Install the Google Tag on all pages of your website',
      'Configure conversion actions in Google Ads (purchases, form fills, calls)',
      'Verify tags are firing correctly using Google Tag Assistant',
      'Optionally, sync HubSpot CRM deal events as offline conversions',
    ],
    benefits: [
      'Smart bidding strategies (Target CPA, Target ROAS) rely entirely on conversion data to function',
      'Offline event sync closes the loop on deals that start with an ad click but convert in your CRM — critical for B2B',
      'Accurate conversion data typically reduces CPA by 20–40% as bidding algorithms improve over time',
    ],
  },
  campaigns: {
    intro: 'Campaigns are the core deliverable of the platform. The guided wizards handle keyword research, ad copy generation, and Google Ads structure automatically.',
    involved: [
      'Brand campaign: exact-match keywords for your brand terms, three fixed headlines, competitor defence structure',
      'Prospecting campaign: multi-keyword targeting across the top, middle, and bottom of the funnel',
      'Each campaign is created in Google Ads via the API — no manual setup required',
    ],
    benefits: [
      'Brand campaigns protect your branded searches from competitor bidding and typically deliver 4–6x ROAS',
      'Prospecting campaigns drive new customer acquisition — the primary growth lever for most advertisers',
      'Wizard-built campaigns follow Google best practices by default, improving ad strength and quality score from launch',
    ],
  },
  assets: {
    intro: 'Ad assets (formerly extensions) expand your ad in search results by adding extra links, callouts, and product information. They are served automatically by Google when relevant.',
    involved: [
      'Sitelinks: links to specific pages on your website (e.g. Pricing, Case Studies, Contact)',
      'Callouts: short text snippets highlighting USPs (e.g. "No lock-in contracts", "24/7 support")',
      'Structured snippets: lists of products, services, or features',
    ],
    benefits: [
      'Assets increase the physical size of your ad in search results, improving visibility',
      'Google reports average CTR uplifts of 10–15% from well-configured assets',
      'Assets are free to add and apply across all campaigns — high ROI for minimal effort',
    ],
  },
  'budget-cap': {
    intro: 'The organisation budget cap sets an absolute ceiling on monthly spend across all campaigns, independent of individual campaign budgets.',
    involved: [
      'Set the maximum monthly spend on the Business page',
      'The platform will alert you when spend approaches the cap',
      'Individual campaign budgets still apply — the cap is an additional safety net',
    ],
    benefits: [
      'Prevents overspend at the account level — critical for agencies managing client accounts with fixed retainers',
      'Protects against runaway spend if a campaign budget is accidentally set too high',
      'Provides clear financial governance for teams where the campaign manager and budget owner are different people',
    ],
  },
  review: {
    intro: 'Before going live, all generated headlines and descriptions should be reviewed for brand voice, accuracy and compliance.',
    involved: [
      'Review all headlines (up to 15 per ad) and descriptions (up to 4 per ad)',
      'Check for brand tone consistency and factual accuracy',
      'Ensure no claims that could breach Google\'s advertising policies',
      'Approve or edit copy directly within each campaign',
    ],
    benefits: [
      'Ad copy is the first thing a potential customer sees — it directly determines click-through rate',
      'Catching off-brand or inaccurate copy before launch prevents negative first impressions at scale',
      'Google\'s ad strength score improves with diverse, high-quality copy, which reduces cost-per-click over time',
    ],
  },
  live: {
    intro: 'Going live publishes your campaigns to Google Ads. Ads enter Google\'s review process before serving, which typically takes a few hours for established accounts.',
    involved: [
      'Set campaigns from DRAFT to PUBLISHED',
      'Google reviews ad copy and landing pages (usually under 24 hours)',
      'Bidding begins as soon as ads are approved',
      'The first 7–14 days are a learning period for Google\'s algorithms',
    ],
    benefits: [
      'Your ads start competing in auctions and driving traffic immediately after approval',
      'Smart bidding strategies begin collecting conversion data from the first click',
      'The sooner campaigns go live, the sooner the learning period completes and performance stabilises',
    ],
  },
  'search-terms': {
    intro: 'The search terms report shows exactly which user queries triggered your ads and how they performed. New campaigns almost always attract some irrelevant traffic in the first weeks.',
    involved: [
      'Review the search terms report in the Campaigns section',
      'Identify queries that are irrelevant to your business or product',
      'Add irrelevant terms as negative keywords at the campaign or ad group level',
      'Repeat weekly for the first month, then monthly thereafter',
    ],
    benefits: [
      'Negative keywords prevent wasted spend on irrelevant searches — typically the highest-ROI optimisation activity',
      'Improving match quality raises conversion rate and lowers CPA across all campaigns',
      'Regular review also surfaces new keyword opportunities you may have missed in initial setup',
    ],
  },
  'performance-review': {
    intro: 'After the first 7 days, enough data has accumulated to evaluate whether campaigns are tracking toward their targets.',
    involved: [
      'Review CTR, CPA, conversion volume, and impression share in the Reports section',
      'Compare actual CPA against your target CPA',
      'Check impression share — low impression share often indicates budget constraints or quality issues',
      'Generate your first weekly performance report',
    ],
    benefits: [
      'Early performance reviews catch structural or bidding issues before they compound into weeks of wasted budget',
      'Impression share data reveals whether budget or quality score is the primary constraint',
      'Reports can be shared with clients or stakeholders directly from the platform',
    ],
  },
  'optimizations-review': {
    intro: 'The platform continuously analyses your campaigns and generates prioritised recommendations based on performance data and Google Ads best practices.',
    involved: [
      'Review the first batch of optimization recommendations in the Optimizations section',
      'Each recommendation includes the reason it was flagged and the expected impact',
      'Approve recommendations you agree with — they are applied automatically via the Google Ads API',
      'Recommendations you skip are logged and can be revisited later',
    ],
    benefits: [
      'Optimizations compound over time — acting on recommendations in the first 30 days significantly improves account performance',
      'Each approved optimization improves Google\'s quality signals, reducing cost-per-click and improving ad rank',
      'The system surfaces issues that would take a human analyst hours to find manually',
    ],
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function renderDescription(parts: DescPart[], faded: boolean) {
  return (
    <>
      {parts.map((part, i) => {
        if ('to' in part) {
          return (
            <Link
              key={i}
              to={part.to}
              className={faded ? 'text-gray-400 underline' : 'text-[#290BD9] hover:text-[#2309C5]'}
            >
              {part.label}
            </Link>
          );
        }
        return <span key={i}>{part.text}</span>;
      })}
    </>
  );
}

// ─── Checkbox ─────────────────────────────────────────────────────────────────

function Checkbox({
  checked,
  onChange,
  size = 'lg',
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  size?: 'lg' | 'sm';
  disabled?: boolean;
}) {
  const dim = size === 'lg' ? 'h-8 w-8' : 'h-5 w-5';
  const iconSize = size === 'lg' ? 'h-4 w-4' : 'h-3 w-3';

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      className={cn(
        'shrink-0 rounded flex items-center justify-center border-2 transition-colors',
        dim,
        checked ? 'bg-green-600 border-green-600' : 'bg-white border-gray-300 hover:border-green-600',
        disabled && !checked && 'opacity-40 cursor-not-allowed',
        !disabled && 'cursor-pointer',
      )}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && <Check className={cn(iconSize, 'text-white stroke-[3]')} />}
    </button>
  );
}

// ─── Step Row ─────────────────────────────────────────────────────────────────

function StepRow({
  step,
  stepNumber,
  checked,
  childChecked,
  isSelected,
  onToggle,
  onToggleChild,
  onSelect,
}: {
  step: Step;
  stepNumber: number;
  checked: boolean;
  childChecked: Record<string, boolean>;
  isSelected: boolean;
  onToggle: () => void;
  onToggleChild: (childId: string) => void;
  onSelect: () => void;
}) {
  const isComplete = step.forceComplete || checked;

  return (
    <div
      className={cn(
        'py-5 px-6 cursor-pointer transition-colors',
        isSelected ? 'bg-[#290BD9]/10' : 'hover:bg-gray-50',
      )}
      onClick={onSelect}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isComplete}
            onChange={step.forceComplete ? () => {} : onToggle}
            disabled={step.forceComplete}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold uppercase text-gray-400 tracking-wide w-5 shrink-0">
              {stepNumber}
            </span>
            <span className={cn('text-base font-semibold', isComplete ? 'text-gray-500 line-through' : 'text-gray-900')}>
              {step.title}
            </span>
          </div>
          <p className={cn('text-sm mt-0.5 ml-7', isComplete ? 'text-gray-400' : 'text-gray-500')}>
            {renderDescription(step.description, isComplete)}
          </p>

          {/* Sub-steps */}
          {step.children && (
            <div className="mt-3 ml-7 space-y-2.5" onClick={(e) => e.stopPropagation()}>
              {step.children.map((child) => {
                const childComplete = childChecked[child.id] ?? false;
                return (
                  <div key={child.id} className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <Checkbox
                        checked={childComplete}
                        onChange={() => onToggleChild(child.id)}
                        size="sm"
                        disabled={child.locked}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-sm font-medium', childComplete ? 'text-gray-400 line-through' : 'text-gray-900')}>
                          {child.title}
                        </span>
                        {child.optional && (
                          <span className="text-sm text-gray-400 font-normal">Optional</span>
                        )}
                        {child.locked && (
                          <span className="text-sm text-gray-400 font-normal">Coming soon</span>
                        )}
                      </div>
                      <p className={cn('text-sm mt-0.5', childComplete ? 'text-gray-400' : 'text-gray-500')}>
                        {renderDescription(child.description, childComplete)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function StepDetailPanel({ step, detail }: { step: Step; detail: StepDetail }) {
  return (
    <TabCardPanel
      tabs={[{ id: 'detail', label: step.title, isActive: true }]}
    >
      <div className="space-y-6">
        <p className="text-sm text-gray-700 leading-relaxed">{detail.intro}</p>

        <div>
          <p className="text-sm font-semibold uppercase text-gray-500 tracking-wide mb-3">What's involved</p>
          <ul className="space-y-2">
            {detail.involved.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-400 shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-sm font-semibold uppercase text-gray-500 tracking-wide mb-3">Benefits</p>
          <ul className="space-y-2">
            {detail.benefits.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-green-600 shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </TabCardPanel>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const SETUP_TABS = ['onboarding', 'strategy'] as const;
type SetupTab = (typeof SETUP_TABS)[number];

// Purple variant of `tabButtonClass` (#6011): flags the Strategy tab as distinct from the
// neutral/cobalt Onboarding tab, reusing the strategy section's violet accents.
const strategyTabClass = (isActive: boolean) =>
  cn(
    'relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all',
    isActive
      ? 'bg-white text-[#6D28D9] border border-[#C4B5FD] border-b-transparent'
      : 'bg-[#F5F3FF] text-[#7C3AED] border border-transparent border-b-[#C4B5FD] hover:bg-[#EDE9FE]',
  );

// ─── Page ─────────────────────────────────────────────────────────────────────

function SetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [organizationData, setOrganizationData] = useAtom(organizationDataAtom);
  const orgId = organizationData?.id;

  const { activeTab, handleTabChange } = useTabPersistence<SetupTab>(
    selectedSetupTabAtom,
    SETUP_TABS,
    'onboarding',
    (id, replace) => navigate({ to: '/setup', hash: id, replace }),
    location.hash,
  );

  // The ticks are the organisation's, not this page's (#6708): they are read
  // from the organisation each render and written back on every change, so
  // leaving the page, switching organisation or reloading finds them as left.
  // The write is optimistic: the organisation in the atom takes the new list
  // at once, and a refused save puts the previous list back.
  const ticks = ticksFromChecklist(organizationData?.onboarding?.setup_checklist);
  const { checked, childChecked } = ticks;
  const saveChecklist = useSaveSetupChecklistMutation(orgId ?? 0);
  const [selectedStepId, setSelectedStepId] = useState<string>(STEPS[0].id);

  const completedCount = STEPS.filter((s) => s.forceComplete || checked[s.id]).length;
  const totalCount = STEPS.length;

  const applyTicks = (next: SetupTicks) => {
    if (!organizationData) return;
    const before = organizationData;
    const checklist = checklistFromTicks(next);
    setOrganizationData({ ...before, onboarding: { ...before.onboarding, setup_checklist: checklist } });
    saveChecklist.mutate(checklist, {
      onError: () => {
        setOrganizationData(before);
        toast({
          title: 'Setup step not saved',
          description: 'The tick could not be saved. Please try again.',
          variant: 'destructive',
        });
      },
    });
  };
  const toggle = (id: string) => applyTicks({ ...ticks, checked: { ...checked, [id]: !checked[id] } });
  const toggleChild = (childId: string) =>
    applyTicks({ ...ticks, childChecked: { ...childChecked, [childId]: !childChecked[childId] } });

  const selectedStep = STEPS.find((s) => s.id === selectedStepId) ?? STEPS[0];
  const selectedDetail = STEP_DETAILS[selectedStepId] ?? STEP_DETAILS[STEPS[0].id];

  const isOnboarding = activeTab === 'onboarding';

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>

      <div className="grid grid-cols-12 gap-1">

        {/* ── Left: tabs + active-tab content (full width on Strategy) ───── */}
        <div className={isOnboarding ? 'col-span-8' : 'col-span-12'}>
          <div className="flex items-end gap-1">
            <button className={tabButtonClass(isOnboarding)} onClick={() => handleTabChange('onboarding')}>
              Onboarding
            </button>
            <button className={strategyTabClass(!isOnboarding)} onClick={() => handleTabChange('strategy')}>
              Strategy
            </button>
          </div>

          {isOnboarding ? (
            <Card className="rounded-lg rounded-tl-none border bg-white">
              <CardContent className="p-0 divide-y divide-gray-100">
                {/* Progress bar */}
                <div className="px-6 py-4 flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase text-gray-500 tracking-wide">
                    {completedCount} / {totalCount} complete
                  </span>
                  <div className="w-48 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-green-600 transition-all duration-300"
                      style={{ width: `${(completedCount / totalCount) * 100}%` }}
                    />
                  </div>
                </div>

                {STEPS.map((step, index) => (
                  <StepRow
                    key={step.id}
                    step={step}
                    stepNumber={index + 1}
                    checked={checked[step.id] ?? false}
                    childChecked={childChecked}
                    isSelected={step.id === selectedStepId}
                    onToggle={() => toggle(step.id)}
                    onToggleChild={toggleChild}
                    onSelect={() => setSelectedStepId(step.id)}
                  />
                ))}
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-lg rounded-tl-none border bg-white overflow-hidden">
              {/* Goals is org-scoped; remount on org change to reseed atoms + reset local state. */}
              <CardContent className="p-0">
                <GoalsPage key={orgId ?? 'no-org'} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── Right: step detail 4/12 (Onboarding only) ─────────────────── */}
        {isOnboarding && (
          <div className="col-span-4 sticky top-4 self-start">
            <StepDetailPanel step={selectedStep} detail={selectedDetail} />
          </div>
        )}

      </div>
    </div>
  );
}
